export const evidenceClasses = ["official", "independent", "vendor_claim", "rumor"] as const;
export type EvidenceClass = (typeof evidenceClasses)[number];

export const claimStatuses = ["current", "superseded", "retracted"] as const;
export type ClaimStatus = (typeof claimStatuses)[number];

export const entityKinds = ["model", "research_result", "agent_runtime"] as const;
export type EntityKind = (typeof entityKinds)[number];

export type Source = {
  title: string;
  publisher: string;
  url: string;
  sourceKind: "primary" | "independent" | "community";
  publishedAt: string;
  retrievedAt: string;
};

export type Claim = {
  id: string;
  entityId: string;
  title: string;
  summary: string;
  effectiveAt: string;
  verifiedAt: string;
  evidenceClass: EvidenceClass;
  status: ClaimStatus;
  topics: string[];
  sources: Source[];
};

export type Entity = {
  id: string;
  name: string;
  organization: string;
  kind: EntityKind;
  summary: string;
};

export type ChangeEntry = {
  id: string;
  claimId: string;
  changedAt: string;
  action: "added" | "updated" | "superseded" | "retracted";
  note: string;
};

export type KnowledgeBase = {
  claims: Claim[];
  entities: Entity[];
  changes: ChangeEntry[];
  verifiedThrough: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const requireString = (record: Record<string, unknown>, key: string): string => {
  const value = record[key];
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`${key} must be a non-empty string`);
  }
  return value;
};

const requireTimestamp = (record: Record<string, unknown>, key: string): string => {
  const value = requireString(record, key);
  if (Number.isNaN(Date.parse(value))) throw new Error(`${key} must be an ISO timestamp`);
  return value;
};

const parseSource = (value: unknown): Source => {
  if (!isRecord(value)) throw new Error("source must be an object");
  const url = requireString(value, "url");
  if (!url.startsWith("https://")) throw new Error("source URL must use https");
  const sourceKind = requireString(value, "sourceKind");
  if (!["primary", "independent", "community"].includes(sourceKind)) {
    throw new Error(`invalid source kind: ${sourceKind}`);
  }
  return {
    title: requireString(value, "title"),
    publisher: requireString(value, "publisher"),
    url,
    sourceKind: sourceKind as Source["sourceKind"],
    publishedAt: requireTimestamp(value, "publishedAt"),
    retrievedAt: requireTimestamp(value, "retrievedAt"),
  };
};

const parseClaim = (value: unknown): Claim => {
  if (!isRecord(value)) throw new Error("claim must be an object");
  const evidenceClass = requireString(value, "evidenceClass");
  const status = requireString(value, "status");
  if (!evidenceClasses.includes(evidenceClass as EvidenceClass)) {
    throw new Error(`invalid evidence class: ${evidenceClass}`);
  }
  if (!claimStatuses.includes(status as ClaimStatus)) {
    throw new Error(`invalid claim status: ${status}`);
  }
  if (!Array.isArray(value.topics) || !value.topics.every((topic) => typeof topic === "string")) {
    throw new Error("topics must be a string array");
  }
  if (!Array.isArray(value.sources) || value.sources.length === 0) {
    throw new Error("claim must have at least one source");
  }
  return {
    id: requireString(value, "id"),
    entityId: requireString(value, "entityId"),
    title: requireString(value, "title"),
    summary: requireString(value, "summary"),
    effectiveAt: requireTimestamp(value, "effectiveAt"),
    verifiedAt: requireTimestamp(value, "verifiedAt"),
    evidenceClass: evidenceClass as EvidenceClass,
    status: status as ClaimStatus,
    topics: value.topics,
    sources: value.sources.map(parseSource),
  };
};

const parseEntity = (value: unknown): Entity => {
  if (!isRecord(value)) throw new Error("entity must be an object");
  const kind = requireString(value, "kind");
  if (!entityKinds.includes(kind as EntityKind)) throw new Error(`invalid entity kind: ${kind}`);
  return {
    id: requireString(value, "id"),
    name: requireString(value, "name"),
    organization: requireString(value, "organization"),
    kind: kind as EntityKind,
    summary: requireString(value, "summary"),
  };
};

const parseChange = (value: unknown): ChangeEntry => {
  if (!isRecord(value)) throw new Error("change must be an object");
  const action = requireString(value, "action");
  if (!["added", "updated", "superseded", "retracted"].includes(action)) {
    throw new Error(`invalid change action: ${action}`);
  }
  return {
    id: requireString(value, "id"),
    claimId: requireString(value, "claimId"),
    changedAt: requireTimestamp(value, "changedAt"),
    action: action as ChangeEntry["action"],
    note: requireString(value, "note"),
  };
};

const assertUniqueIds = (label: string, records: Array<{ id: string }>) => {
  const ids = new Set<string>();
  for (const record of records) {
    if (!/^[a-z0-9-]+$/.test(record.id)) throw new Error(`${label} id is invalid: ${record.id}`);
    if (ids.has(record.id)) throw new Error(`duplicate ${label} id: ${record.id}`);
    ids.add(record.id);
  }
};

export const parseKnowledgeBase = (input: {
  claims: unknown;
  entities: unknown;
  changes: unknown;
}): KnowledgeBase => {
  if (!Array.isArray(input.claims) || !Array.isArray(input.entities) || !Array.isArray(input.changes)) {
    throw new Error("claims, entities, and changes must be arrays");
  }
  const claims = input.claims.map(parseClaim);
  const entities = input.entities.map(parseEntity);
  const changes = input.changes.map(parseChange);
  assertUniqueIds("claim", claims);
  assertUniqueIds("entity", entities);
  assertUniqueIds("change", changes);

  const entityIds = new Set(entities.map((entity) => entity.id));
  const claimIds = new Set(claims.map((claim) => claim.id));
  for (const claim of claims) {
    if (!entityIds.has(claim.entityId)) throw new Error(`unknown entity: ${claim.entityId}`);
  }
  for (const change of changes) {
    if (!claimIds.has(change.claimId)) throw new Error(`unknown claim: ${change.claimId}`);
  }

  const verifiedThrough = claims.reduce(
    (latest, claim) => (claim.verifiedAt > latest ? claim.verifiedAt : latest),
    "1970-01-01T00:00:00.000Z",
  );
  return { claims, entities, changes, verifiedThrough };
};

